import { useContext, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Button } from '@zillow/constellation-10';

import { LoadingButton } from '@zg-rentals/loading-components';
import { logError } from '@zg-rentals/log-error';
import { useRentalManagerUserPropertySummaryWebApiHandler_leases } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { default as dashboardActions } from '../../containers/DashboardPage/actions';
import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { VERTICAL_DISPLAY_TYPE } from '../../constants/PropertiesConstants';
import TimeConstants from '../../constants/TimeConstants';
import ManageTenantsCSEvents from '../../model/ClickstreamEvent/ManageTenantsCSEvents';

import ErrorMessagesAlert from '../../components/ErrorMessagesAlert';
import { DashboardContext } from '../../containers/DashboardPage/context';
import ConnectedLeases from './components/ConnectedLeases';
import EmptyLeasesCard from './components/EmptyLeasesCard';
import EmptyLeasesCardSimple from './components/EmptyLeasesCardSimple';

import { RMW_PATH } from '../../pathsManifest';
import ConnectedModuleCard from '../ConnectedModuleCard';
import { StyledConnectedModuleCard } from './styles';

import type { State as DashboardState } from '../../containers/DashboardPage/reducer';

const MAX_LEASE_CARDS_DISPLAY = 2;

type Props = {
  displayType?: VERTICAL_DISPLAY_TYPE;
  setIsLeasePresent?: (isLeasePresent: boolean) => void;
  setIsEligibleForGeneratedLease?: (isEligible: boolean) => void;
};

const ConnectedLeasesModule = ({
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  setIsLeasePresent,
  setIsEligibleForGeneratedLease,
}: Props) => {
  const { dashboardState, reducerDispatch: dashboardReducerDispatch } = useContext(DashboardContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const { user, propertyState } = useSelector(
    (state) => ({
      user: state.user,
      propertyState: state.properties.currentProperty.state,
    }),
    shallowEqual,
  );

  // use isLoading condition in order apply conditional logic on the initial request
  const { data, error, isLoading } = useRentalManagerUserPropertySummaryWebApiHandler_leases(
    { listingAlias: listingId },
    { enabled: Boolean(listingId), staleTime: TimeConstants.SECONDS_10, retry: false },
  );

  const { activePath: activeDashboardPath, fluidSmallLTE }: Partial<DashboardState> = dashboardState;

  const hasData = useMemo(() => {
    return data?.leaseCards?.length ? data.leaseCards.length > 0 : false;
  }, [data?.leaseCards]);

  const isThisActiveDashboardPath = useMemo(() => {
    return activeDashboardPath === RMW_PATH.DASHBOARD_LEASES;
  }, [activeDashboardPath]);

  /**
   * Sets the border state for less than or equal to SM
   *
   * this component hides the border when the component is not empty
   */
  const hideBorder = useMemo(() => {
    if (fluidSmallLTE !== undefined) {
      const hideBorder = fluidSmallLTE && Boolean(data?.leaseCards?.length);

      return hideBorder;
    }
  }, [fluidSmallLTE, data?.leaseCards?.length]);

  useEffect(() => {
    if (error) {
      logError({
        error,
        context: 'getLeasesSummary',
      });
    }
  }, [error]);

  useEffect(() => {
    if (isThisActiveDashboardPath === false) {
      // do not set dashboard state fluid value if the active path is not leases module
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setIsLeasePresent && setIsLeasePresent(hasData);

    dashboardActions.updateAnyProperty({
      reducerDispatch: dashboardReducerDispatch,
      data: {
        fluidSmallLTE: hasData,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThisActiveDashboardPath, isLoading, error]);

  useEffect(() => {
    if (!isLoading && data) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      setIsEligibleForGeneratedLease && setIsEligibleForGeneratedLease(data?.isEligibleForGeneratedLease ?? false);
    }
  }, [isLoading, data, setIsEligibleForGeneratedLease]);

  if (error) {
    const partialErrorMessages = (error as { error?: { data?: { partialMessages?: Array<{ text: string }> } } })?.error
      ?.data?.partialMessages || [
      // eslint-disable-next-line constellation/feedback-messages
      { text: 'Sorry, an error occurred.' },
    ];

    if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return <ErrorMessagesAlert appearance="warning" errorMessages={partialErrorMessages} />;
    }

    return (
      <StyledConnectedModuleCard title="Leases">
        <ErrorMessagesAlert appearance="warning" errorMessages={partialErrorMessages} />
      </StyledConnectedModuleCard>
    );
  }

  const {
    onboardingModule = {
      title: '',
      description: '',
      primaryCtaText: '',
      primaryCtaUrl: '',
      secondaryCtaText: '',
      secondaryCtaUrl: '',
    },
    leaseCards: aLeaseCards = [],
    dashboardLinkText,
    urlPath: dashboardUrlPath,
    ctaButtons = [],
    isEligibleForGeneratedLease = false,
  } = data || {};

  const leaseCards = aLeaseCards.slice(0, MAX_LEASE_CARDS_DISPLAY);

  const handleViewAllLeasesClick = () => {
    ManageTenantsCSEvents.ViewAllLeases({ user });
  };

  const renderFullType = () => {
    return (
      <div data-testid="ConnectedLeasesModule-Full">
        {(leaseCards && leaseCards.length) || isLoading ? (
          <ConnectedModuleCard
            className="ConnectedLeasesModule"
            title="Leases"
            ctaComponent={
              leaseCards && leaseCards.length ? (
                <Button
                  asChild
                  size="sm"
                  tone="neutral"
                  emphasis="outlined"
                  onClick={handleViewAllLeasesClick}
                  data-eventid={ManageTenantsCSEvents.ViewAllLeases.data_event_id}
                >
                  <a href={dashboardUrlPath}>{dashboardLinkText}</a>
                </Button>
              ) : (
                Boolean(isLoading) && (
                  <LoadingButton isLoading={true} loadingWidth="60px">
                    Loading
                  </LoadingButton>
                )
              )
            }
            hideBorderOnMobile={hideBorder}
          >
            <ConnectedLeases leaseCards={leaseCards} ctaButtons={ctaButtons} isLoading={isLoading} />
          </ConnectedModuleCard>
        ) : (
          <EmptyLeasesCard {...onboardingModule} />
        )}
      </div>
    );
  };

  const renderSimpleType = () => {
    return (
      <div data-testid="ConnectedLeasesModule-Simple">
        {leaseCards && leaseCards.length ? (
          <ConnectedLeases leaseCards={leaseCards} ctaButtons={ctaButtons} displayType={displayType} />
        ) : (
          <EmptyLeasesCardSimple
            isLoading={isLoading}
            isEligibleForGeneratedLease={isEligibleForGeneratedLease}
            propertyState={propertyState}
          />
        )}
      </div>
    );
  };

  return (
    <div data-testid="ConnectedLeasesModule">
      {displayType === VERTICAL_DISPLAY_TYPE.SIMPLE ? renderSimpleType() : renderFullType()}
    </div>
  );
};

export default ConnectedLeasesModule;
